import * as Sentry from '@sentry/nextjs';
import { deleteToken, getToken, onMessage, Unsubscribe } from 'firebase/messaging';
import { queryClientAtom } from 'jotai-tanstack-query';

import { getFirebaseMessaging } from 'src/config/firebase';
import { NOTIFICATION_QUERY_KEYS } from 'src/hooks/useNotificationQuery';
import store from 'src/stores';
import { statusAtom } from 'src/stores/match/atoms';
import { showToastAtom } from 'src/stores/toast/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { STATUS } from 'src/types/Match';
import { ToastType } from 'src/types/Toast';
import { ControlledPromise } from 'src/utils/controlled-promise';
import { DEEPLINK_FRAGMENT_PATH, deeplinkToFragment } from 'src/utils/deeplink';
import { pushEvent } from 'src/utils/event-gateway';

import { registerFcmAPI } from './apis';
import { extractNotification, parsePayload } from './fcm-message-parser';

const FCM_SW_PATH = '/firebase-messaging-sw.js';
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

const FCM_MESSAGE_TYPE = {
  GCM_NOTIFICATION: 'GcmNotification',
  // DM 메시지 수신 시 사용
  GCM_TEXT_MESSAGE: 'GcmTextMessage',
  /**
   * 클라에서 핸들링 할 작업이 없는 일반화된 푸시는 모두 FcmGeneralMessage로 전송됨
   * 관련 스레드 - https://hpcnt.slack.com/archives/C02SQS5AJDR/p1773642971511659
   */
  FCM_GENERAL_MESSAGE: 'FcmGeneralMessage',
} as const;

// https://github.com/hyperconnect/azar-api/blob/develop/src/main/java/com/azarlive/api/event/fcm/FcmGeneralMessage.java
interface FcmParsedPayload {
  userId: string;
  title?: string;
  message: string;
  buttonText?: string;
  iconImageUrl?: string;
  deeplink?: string;
  notifyDurationMs: number;
  channelId: string;
  attributionData?: string;
  soundOn: boolean;
  vibrateOn: boolean;
  foregroundNotificationMode: string;
  backgroundNotificationMode: string;
  refreshNotificationCenter: boolean;
}

interface GcmNotificationPayload {
  notificationType?: string;
}

function invalidateNotifications() {
  const queryClient = store.get(queryClientAtom);
  queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.all });
}

function handleNotificationCenterUpdate(data?: Record<string, string>) {
  const type = data?.type;

  if (type === FCM_MESSAGE_TYPE.GCM_NOTIFICATION) {
    const parsed = parsePayload<GcmNotificationPayload>(data, FCM_MESSAGE_TYPE.GCM_NOTIFICATION);
    if (parsed?.notificationType === 'CENTER') {
      invalidateNotifications();
      return;
    }
  }

  if (type === FCM_MESSAGE_TYPE.FCM_GENERAL_MESSAGE) {
    const parsed = parsePayload<FcmParsedPayload>(data, FCM_MESSAGE_TYPE.FCM_GENERAL_MESSAGE);
    if (parsed?.refreshNotificationCenter) {
      invalidateNotifications();
      return;
    }
  }
}

function trackPushClickEvent(pushId?: string, title?: string) {
  pushEvent({
    eventType: EVENT_TYPE.PUSH_NOTIFICATION,
    eventName: EVENT_NAME.ANYWHERE__CLICK_PUSH_MESSAGE,
    eventParams: {
      action_category: 'click',
      tab: 'anywhere',
      page: 'anywhere',
      target: 'push_message',
      push_id: pushId,
      title,
    },
  });
}

/**
 * openWindow로 새 탭이 열린 경우, 서비스 워커가 URL query 파라미터로 클릭 정보를 전달한다.
 * 페이지 로드 후 파라미터를 읽어 이벤트를 로깅하고, URL에서 파라미터를 제거한다.
 */
function consumePushClickFromURL() {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const pushId = params.get('fcm_push_id');
  const title = params.get('fcm_title');

  if (!pushId) return;

  trackPushClickEvent(pushId, title || undefined);

  params.delete('fcm_push_id');
  params.delete('fcm_title');
  const query = params.toString();
  const newUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
  window.history.replaceState(null, '', newUrl);
}

let fcmToken: string | null = null;
let foregroundUnsubscribe: Unsubscribe | null = null;
let swMessageHandler: ((event: MessageEvent) => void) | null = null;
let cleanupControlledPromise: ControlledPromise<void> | null = null;

export function getFcmToken(): string | null {
  return fcmToken;
}

async function registerFcmServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;

  return navigator.serviceWorker.register(FCM_SW_PATH, { scope: FCM_SW_SCOPE });
}

/**
 * SW가 activated 상태가 될 때까지 대기한다.
 * installing 또는 waiting 상태일 수 있으므로 둘 다 확인한다.
 * SW 측 skipWaiting()으로 waiting 상태는 통상 발생하지 않지만, 방어적으로 처리한다.
 * redundant 상태도 처리하여 SW가 대체된 경우 Promise가 무한대기하지 않도록 한다.
 */
function waitForSwActivation(registration: ServiceWorkerRegistration): Promise<void> {
  const pendingSw = registration.installing || registration.waiting;
  if (!pendingSw || pendingSw.state === 'activated' || pendingSw.state === 'redundant')
    return Promise.resolve();

  return new Promise<void>((resolve) => {
    pendingSw.addEventListener('statechange', function handler() {
      if (this.state === 'activated' || this.state === 'redundant') {
        this.removeEventListener('statechange', handler);
        resolve();
      }
    });
  });
}

export async function cleanupPushNotification(): Promise<void> {
  const controlled = new ControlledPromise<void>();
  cleanupControlledPromise = controlled;

  try {
    if (foregroundUnsubscribe) {
      foregroundUnsubscribe();
      foregroundUnsubscribe = null;
    }

    if (swMessageHandler) {
      navigator.serviceWorker?.removeEventListener('message', swMessageHandler);
      swMessageHandler = null;
    }

    const messaging = await getFirebaseMessaging();
    if (messaging) {
      await deleteToken(messaging);
    }
  } catch (error) {
    Sentry.captureException(error, { tags: { feature: 'fcm-push-cleanup' } });
  } finally {
    fcmToken = null;
    controlled.resolve();
    if (cleanupControlledPromise === controlled) cleanupControlledPromise = null;
  }
}

export const initializePushNotification = (() => {
  let initializing = false;
  return async (): Promise<void> => {
    if (initializing) return;
    initializing = true;
    try {
      if (!process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY) {
        throw new Error('NEXT_PUBLIC_FIREBASE_VAPID_KEY is not defined');
      }
      const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

      if (cleanupControlledPromise && !cleanupControlledPromise.ended) {
        await cleanupControlledPromise.promise;
      }
      if (typeof window === 'undefined' || !('Notification' in window)) return;

      consumePushClickFromURL();

      const permission = Notification.permission;
      if (permission === 'denied') return;

      if (permission === 'default') {
        const result = await Notification.requestPermission();
        if (result !== 'granted') return;
      }

      const messaging = await getFirebaseMessaging();
      if (!messaging) return;

      const swRegistration = await registerFcmServiceWorker();
      if (!swRegistration) return;

      await waitForSwActivation(swRegistration);

      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swRegistration,
      });

      if (!token) return;

      if (cleanupControlledPromise) return;

      const prevToken = fcmToken;

      if (token !== prevToken) {
        await registerFcmAPI(token);
      }

      fcmToken = token;

      if (swMessageHandler) {
        navigator.serviceWorker.removeEventListener('message', swMessageHandler);
      }

      swMessageHandler = (event: MessageEvent) => {
        if (event.data?.type !== 'NOTIFICATION_CLICK') return;

        const { messageThreadId, deeplink, pushId, title } = event.data;

        trackPushClickEvent(pushId, title);

        // 매치중일때는 토스트만 노출하고 아무 동작하지 않음 (https://hpcnt.slack.com/archives/C02SQS5AJDR/p1773742093099009?thread_ts=1773735574.133799&cid=C02SQS5AJDR)
        if (store.get(statusAtom) !== STATUS.INITIAL) {
          store.set(showToastAtom, {
            type: ToastType.INFO,
            message: 'push.noti_in_match',
          });
          return;
        }

        if (messageThreadId) {
          window.location.hash = `${DEEPLINK_FRAGMENT_PATH.MESSAGES}?messageThreadId=${messageThreadId}`;
        } else if (deeplink) {
          const fragment = deeplinkToFragment(deeplink);
          if (fragment) {
            window.location.hash = fragment;
          }
        }
      };

      navigator.serviceWorker.addEventListener('message', swMessageHandler);

      if (foregroundUnsubscribe) {
        foregroundUnsubscribe();
      }

      foregroundUnsubscribe = onMessage(messaging, (payload) => {
        if (Notification.permission !== 'granted') return;

        handleNotificationCenterUpdate(payload.data);

        const notification = extractNotification(payload.data, payload.notification);
        if (!notification.title) return;

        swRegistration.showNotification(notification.title, {
          body: notification.body || '',
          icon: notification.icon || '/azar/icon-192.png',
          data: {
            messageThreadId: notification.messageThreadId,
            deeplink: notification.deeplink,
            ...payload.data,
          },
        });
      });
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: 'fcm-push' } });
    } finally {
      initializing = false;
    }
  };
})();

export function restorePushNotification(): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;

  if (navigator.permissions?.query) {
    navigator.permissions
      .query({ name: 'notifications' as PermissionName })
      .then((status) => {
        if (status.state === 'granted') {
          initializePushNotification();
        }
      })
      .catch(() => {
        if (Notification.permission === 'granted') {
          initializePushNotification();
        }
      });
  } else if (Notification.permission === 'granted') {
    initializePushNotification();
  }
}
